import { ReactElement, useCallback } from 'react';
import { createFetchContext } from '../../../../components/contexts/FetchContextFactory';
import DisplayError from '../../../../components/info/DisplayError';
import Loading from '../../../../components/info/Loading';
import { AttendanceListResponse } from '../types';
import { getAttendanceList } from '../api';
import dayjs from 'dayjs';
import { useGetDateFilterOptions } from '../hooks/useGetDateFilterOptions';

export interface AttendanceTableProps {
  teacherId: number;
  children: ReactElement;
}

const { FetchProvider, FetchConsumer, useFetch } = createFetchContext<
  {
    initDate: number;
    currentPage: number;
  },
  AttendanceListResponse
>();

export const useAttendancesTableFetch = useFetch;

export function AttendanceTableLoader({
  teacherId,
  children,
}: AttendanceTableProps) {
  const dateFilterOptions = useGetDateFilterOptions();
  const request = useCallback(
    (query) => {
      const monthsDiff =
        dateFilterOptions.find((item) => item.value === query.initDate)
          ?.monthsDiff || 0;
      const initDate = dayjs().subtract(monthsDiff, 'month');
      return getAttendanceList(teacherId, query.currentPage, initDate);
    },
    [teacherId, dateFilterOptions],
  );
  return (
    <FetchProvider
      request={request}
      defaultQuery={{
        initDate: dateFilterOptions[0].value,
        currentPage: 0,
      }}
      fetchImmediately
    >
      {children}
    </FetchProvider>
  );
}

export function AttendancesTableConsumer({
  children,
}: {
  children: (props: { data: AttendanceListResponse }) => ReactElement;
}) {
  return (
    <FetchConsumer>
      {({ data, loading, error, refresh }) => {
        if (error) {
          return (
            <DisplayError
              insideCard
              textBody={error.code}
              retryAction={refresh}
              loadingAction={loading}
            />
          );
        }

        if (loading) {
          return (
            <div className="mx-3">
              <Loading insideCard />
            </div>
          );
        }

        if (!data) {
          return (
            <DisplayError
              insideCard
              textBody="Data no cargada"
              retryAction={refresh}
              loadingAction={loading}
            />
          );
        }

        return children({ data });
      }}
    </FetchConsumer>
  );
}
